import { Injectable } from '@angular/core';
import {
  AbstractArTran,
  ArTranEntity,
  BaseArTranLabel,
  BaseArTranLabels,
  CreditArTranType,
  DebitArTranType,
  UnallocatedArTranIdsByClient,
} from '@anduin/api';
import { ConfigState } from '@anduin/config';
import {
  DEFAULT_LOADABLE_STATE,
  Loadable,
  LoadableHelper,
  LoadableStatus,
  Logger,
  VariableTitleCasePipe,
  isEmpty,
  setLoadableInState,
} from '@anduin/shared';
import { Action, NgxsOnInit, Selector, State, StateContext, Store, createSelector } from '@ngxs/store';
import { isEqual } from 'lodash';
import { Observable, of } from 'rxjs';
import { map, switchMap, take, tap } from 'rxjs/operators';
import { ArTranApiService } from 'src/app/api/services/ar-tran-api.service';
import { NewTransactionOption, NewTransactionType, TransactionsSidebarMode, TransactionsTableExternalFilters } from '../models';
import {
  ApplyTransactionsFeatureStateStash,
  ClearLinkableArTrans,
  ClearTransactionsFeatureStateStash,
  CreateTransaction,
  DeactivateTransaction,
  DeselectArTran,
  GetArTranLabels,
  GetLinkableArTrans,
  GetLinkedArTrans,
  GetUnallocatedArTranIds,
  RefreshSelectedArTran,
  SaveTransactionLinks,
  SaveTransactionNote,
  SaveTransactionTotal,
  SelectArTran,
  SetAreTransactionSidebarChangesPending,
  SetPaymentAsNsf,
  SetSelectedNewTransactionOption,
  SetTransactionsFeatureStateContext,
  SetTransactionsFeatureStateStash,
  SetTransactionsSidebarMode,
  UpdateTransaction,
  UpdateTransactionsTableExternalFilters,
} from './ar-management.actions';
import {
  AR_MANAGEMENT_STATE_MODEL_DEFAULT,
  ArManagementCommonStateModel,
  ArManagementFeatureStateContext,
  ArManagementFeatureStateModel,
  ArManagementFeatureStateStashes,
  ArManagementStateModel,
} from './ar-management.model';

export const AR_MANAGEMENT_STATE_SLICE = 'arManagement';

@State<ArManagementStateModel>({
  name: AR_MANAGEMENT_STATE_SLICE,
  defaults: AR_MANAGEMENT_STATE_MODEL_DEFAULT,
})
@Injectable({
  providedIn: 'root',
})
export class ArManagementState implements NgxsOnInit {
  //#region Selectors

  //#region Stash Management

  @Selector()
  static getFeatureStateStashes({ featureStateStashes }: ArManagementStateModel): ArManagementFeatureStateStashes {
    return featureStateStashes;
  }

  @Selector()
  static getActiveStashName({ activeStashName }: ArManagementStateModel): string {
    return activeStashName;
  }

  static getStash(stashName: string): (featureStateStashes: ArManagementFeatureStateStashes) => ArManagementFeatureStateModel {
    return createSelector([ArManagementState.getFeatureStateStashes], (featureStateStashes) => featureStateStashes?.[stashName]);
  }

  //#endregion

  //#region Setup/Mode

  @Selector()
  static getContextLoadable({ context }: ArManagementStateModel): Loadable<ArManagementFeatureStateContext> {
    return context;
  }

  @Selector([ArManagementState.getContextLoadable])
  static getContextStatus({ status }: Loadable<ArManagementFeatureStateContext>): LoadableStatus {
    return status;
  }

  @Selector([ArManagementState.getContextLoadable])
  static getContext({ data }: Loadable<ArManagementFeatureStateContext>): ArManagementFeatureStateContext {
    return data;
  }

  @Selector([ArManagementState.getContext])
  static getClientIdsScope(context: ArManagementFeatureStateContext): number[] {
    return context?.scope?.clientIds;
  }

  @Selector()
  static getUnallocatedArTranIdsByClientLoadable({
    unallocatedArTranIdsByClient,
  }: ArManagementStateModel): Loadable<UnallocatedArTranIdsByClient> {
    return unallocatedArTranIdsByClient;
  }

  @Selector([ArManagementState.getUnallocatedArTranIdsByClientLoadable])
  static getUnallocatedArTranIdsByClientStatus({ status }: Loadable<UnallocatedArTranIdsByClient>): LoadableStatus {
    return status;
  }

  @Selector([ArManagementState.getUnallocatedArTranIdsByClientLoadable])
  static getUnallocatedArTranIdsByClient({ data }: Loadable<UnallocatedArTranIdsByClient>): UnallocatedArTranIdsByClient {
    return data;
  }

  static getClientUnallocatedArTranIds(clientId: number): (unallocatedArTranIdsByClient: UnallocatedArTranIdsByClient) => number[] {
    return createSelector(
      [ArManagementState.getUnallocatedArTranIdsByClient],
      (unallocatedArTranIdsByClient: UnallocatedArTranIdsByClient) => unallocatedArTranIdsByClient[clientId] ?? [],
    );
  }

  static doesClientHaveUnallocatedArTranIds(clientId: number): (clientUnallocatedArTranIds: number[]) => boolean {
    return createSelector(
      [ArManagementState.getClientUnallocatedArTranIds(clientId)],
      (clientUnallocatedArTranIds: number[]) => clientUnallocatedArTranIds?.length > 0,
    );
  }

  @Selector([ArManagementState.getUnallocatedArTranIdsByClient, ArManagementState.getClientIdsScope])
  static getUnallocatedArTranIds(unallocatedArTranIdsByClient: UnallocatedArTranIdsByClient, clientIds: number[]): number[] {
    if (isEmpty(unallocatedArTranIdsByClient)) {
      return [];
    }

    if (isEmpty(clientIds)) {
      return Object.values(unallocatedArTranIdsByClient).reduce(
        (acc, unallocatedArTranIds) => acc.concat(unallocatedArTranIds),
        new Array<number>(),
      );
    }

    return clientIds.reduce((acc, clientId) => acc.concat(unallocatedArTranIdsByClient[clientId] ?? []), new Array<number>());
  }

  @Selector([ArManagementState.getUnallocatedArTranIds])
  static getUnallocatedArTranCount(unallocatedArTranIds: number[]): number {
    return unallocatedArTranIds?.length;
  }

  @Selector()
  static getRawTransactionsTableExternalFilters({
    transactionsTableExternalFilters,
  }: ArManagementStateModel): TransactionsTableExternalFilters {
    return transactionsTableExternalFilters;
  }

  @Selector([ArManagementState.getRawTransactionsTableExternalFilters, ArManagementState.getClientIdsScope])
  static getTransactionsTableExternalFilters(
    transactionsTableExternalFilters: TransactionsTableExternalFilters,
    clientIds: number[],
  ): TransactionsTableExternalFilters {
    if (isEmpty(clientIds)) {
      return transactionsTableExternalFilters;
    }

    return { ...transactionsTableExternalFilters, clientIds };
  }

  static getTransactionsTableExternalFilter<T extends TransactionsTableExternalFilters, K extends keyof T>(
    filter: K,
  ): (transactionsTableExternalFilters: TransactionsTableExternalFilters) => T[K] {
    return createSelector(
      [ArManagementState.getTransactionsTableExternalFilters],
      (transactionsTableExternalFilters: T) => transactionsTableExternalFilters[filter],
    );
  }

  @Selector()
  static getTransactionsSidebarMode({ transactionsSidebarMode }: ArManagementStateModel): TransactionsSidebarMode {
    return transactionsSidebarMode;
  }

  @Selector()
  static getArTranLabelsLoadable({ arTranLabels }: ArManagementStateModel): Loadable<BaseArTranLabels> {
    return arTranLabels;
  }

  @Selector([ArManagementState.getArTranLabelsLoadable])
  static getArTranLabelsStatus({ status }: Loadable<BaseArTranLabels>): LoadableStatus {
    return status;
  }

  @Selector([ArManagementState.getArTranLabelsLoadable])
  static getArTranLabels({ data }: Loadable<BaseArTranLabels>): BaseArTranLabels {
    return data;
  }

  @Selector([ArManagementState.getArTranLabels, ConfigState.getConfig('lateFeeDisplayName')])
  static getNewTransactionOptions(baseArTranLabels: BaseArTranLabels, lateFeeDisplayName: string): NewTransactionOption[] {
    const newUnlabelledTransactionOptions = ArManagementState.getNewUnlabelledTransactionOptions(lateFeeDisplayName);

    if (baseArTranLabels == undefined) {
      return newUnlabelledTransactionOptions;
    }

    return [newUnlabelledTransactionOptions.find(({ type }) => type === CreditArTranType.RECEIPT)]
      .concat(baseArTranLabels.credit.map((label) => ArManagementState.marshalLabelledNewTransactionOption(CreditArTranType.CREDIT, label)))
      .concat(baseArTranLabels.debit.map((label) => ArManagementState.marshalLabelledNewTransactionOption(DebitArTranType.DEBIT, label)));
  }

  static getNewUnlabelledTransactionOptions(lateFeeDisplayName: string): NewTransactionOption[] {
    return [
      {
        type: CreditArTranType.RECEIPT,
        display: 'Payment',
      },
      {
        type: DebitArTranType.LATE_FEE,
        display: VariableTitleCasePipe.staticTransform(lateFeeDisplayName, undefined, true),
      },
    ];
  }

  static marshalLabelledNewTransactionOption(
    type: CreditArTranType.CREDIT | DebitArTranType.DEBIT,
    { id, name }: BaseArTranLabel,
  ): NewTransactionOption {
    return { type, display: VariableTitleCasePipe.staticTransform(name, ['-'], true), labelId: id };
  }

  @Selector()
  static getSelectedNewTransactionOption({ selectedNewTransactionOption }: ArManagementStateModel): NewTransactionOption {
    return selectedNewTransactionOption;
  }

  @Selector([ArManagementState.getSelectedNewTransactionOption])
  static getSelectedNewTransactionOptionType(selectedNewTransactionOption: NewTransactionOption): NewTransactionType {
    return selectedNewTransactionOption?.type;
  }

  @Selector([ArManagementState.getSelectedNewTransactionOption])
  static getSelectedNewTransactionOptionDisplay(selectedNewTransactionOption: NewTransactionOption): string {
    return selectedNewTransactionOption?.display;
  }

  @Selector([ArManagementState.getSelectedNewTransactionOption])
  static getSelectedNewTransactionOptionLabelId(selectedNewTransactionOption: NewTransactionOption): number {
    return selectedNewTransactionOption?.labelId;
  }

  @Selector()
  static getAreTransactionSidebarChangesPending({ areTransactionSidebarChangesPending }: ArManagementStateModel): boolean {
    return areTransactionSidebarChangesPending;
  }

  //#endregion

  //#region Selected AR Tran

  @Selector()
  static getRawSelectedArTranLoadable({ selectedArTran }: ArManagementStateModel): Loadable<ArTranEntity> {
    return selectedArTran;
  }

  @Selector([ArManagementState.getRawSelectedArTranLoadable])
  static getSelectedArTranStatus({ status }: Loadable<ArTranEntity>): LoadableStatus {
    return status;
  }

  @Selector([ArManagementState.getRawSelectedArTranLoadable])
  static getRawSelectedArTran({ data }: Loadable<ArTranEntity>): ArTranEntity {
    return data;
  }

  @Selector()
  static getSelectedArTranLoadable({ selectedArTran }: ArManagementStateModel): Loadable<AbstractArTran> {
    return { ...selectedArTran, data: AbstractArTran.create(selectedArTran.data) };
  }

  @Selector([ArManagementState.getSelectedArTranLoadable])
  static getSelectedArTran({ data }: Loadable<AbstractArTran>): AbstractArTran {
    return data;
  }

  @Selector([ArManagementState.getSelectedArTranStatus])
  static isArTranSelected(status: LoadableStatus): boolean {
    return status != undefined && status !== LoadableStatus.DEFAULT;
  }

  //#endregion

  //#region Existing and Potential AR Tran Links

  @Selector()
  static getRawLinkedArTransLoadable({ linkedArTrans }: ArManagementStateModel): Loadable<ArTranEntity[]> {
    return linkedArTrans;
  }

  @Selector([ArManagementState.getRawLinkedArTransLoadable])
  static getLinkedArTransStatus({ status }: Loadable<ArTranEntity[]>): LoadableStatus {
    return status;
  }

  @Selector([ArManagementState.getRawLinkedArTransLoadable])
  static getRawLinkedArTrans({ data }: Loadable<ArTranEntity[]>): ArTranEntity[] {
    return data;
  }

  @Selector()
  static getLinkedArTransLoadable({ linkedArTrans }: ArManagementStateModel): Loadable<AbstractArTran[]> {
    return { ...linkedArTrans, data: linkedArTrans.data?.map(AbstractArTran.create).filter(Boolean) };
  }

  @Selector()
  static getRawLinkableArTransLoadable({ linkableArTrans }: ArManagementStateModel): Loadable<ArTranEntity[]> {
    return linkableArTrans;
  }

  @Selector([ArManagementState.getRawLinkableArTransLoadable])
  static getLinkableArTransStatus({ status }: Loadable<ArTranEntity[]>): LoadableStatus {
    return status;
  }

  @Selector()
  static getLinkableArTransLoadable({ linkableArTrans }: ArManagementStateModel): Loadable<AbstractArTran[]> {
    return { ...linkableArTrans, data: linkableArTrans.data?.map(AbstractArTran.create).filter(Boolean) };
  }

  @Selector([ArManagementState.getLinkableArTransLoadable])
  static getLinkableArTrans({ data }: Loadable<AbstractArTran[]>): AbstractArTran[] {
    return data;
  }

  @Selector([ArManagementState.getLinkedArTransLoadable])
  static getLinkedArTrans({ data }: Loadable<AbstractArTran[]>): AbstractArTran[] {
    return data;
  }

  @Selector([ArManagementState.getSelectedArTran, ArManagementState.getLinkedArTrans])
  static getSelectedArTranLinks(selectedArTran: AbstractArTran, linkedArTrans: AbstractArTran[]): AbstractArTran[] {
    return [selectedArTran, ...linkedArTrans];
  }

  //#endregion

  //#endregion

  constructor(
    private readonly store: Store,
    private readonly arTranApiService: ArTranApiService,
    private readonly logger: Logger,
  ) {}

  ngxsOnInit(ctx: StateContext<ArManagementStateModel>): void {
    this.store.dispatch(new GetArTranLabels(true));
  }

  //#region Actions

  //#region Stash Management

  @Action([ApplyTransactionsFeatureStateStash])
  applyTransactionsFeatureStateStash(
    { setState }: StateContext<ArManagementStateModel>,
    { stashName }: ApplyTransactionsFeatureStateStash,
  ): Observable<unknown> | void {
    if (!stashName) {
      this.logger.error('ArManagementState : ApplyTransactionsStateStash : No stash name provided.');
      return;
    }

    const activeStashName = this.store.selectSnapshot(ArManagementState.getActiveStashName);
    if (stashName === activeStashName) {
      this.logger.debug(`ArManagementState : ApplyTransactionsStateStash : Stash '${stashName}' is already active.`);
      return;
    }

    return (activeStashName ? this.store.dispatch(new SetTransactionsFeatureStateStash(activeStashName)) : of(undefined)).pipe(
      tap((_) => {
        const stash = this.store.selectSnapshot(ArManagementState.getStash(stashName));
        if (isEmpty(stash)) {
          this.logger.info(`ArManagementState : ApplyTransactionsStateStash : No stash found for name '${stashName}'.`);
        }

        const arManagementCommonState = this._getArManagementCommonState();

        setState({
          ...AR_MANAGEMENT_STATE_MODEL_DEFAULT,
          ...arManagementCommonState,
          ...(stash ?? {}),
          activeStashName: stashName,
          context: DEFAULT_LOADABLE_STATE, // set context back to default, will be refreshed in SetTransactionsFeatureStateContext
        });
      }),
      switchMap((_) => this.store.dispatch(new ClearTransactionsFeatureStateStash(stashName))), // TODO: Test this. Added post defactorialization.
    );
  }

  @Action([SetTransactionsFeatureStateContext])
  setTransactionsFeatureStateContext(
    { patchState }: StateContext<ArManagementStateModel>,
    { context$ }: SetTransactionsFeatureStateContext,
  ): Observable<unknown> {
    patchState({ context: { status: LoadableStatus.LOADING } });

    return context$.pipe(take(1), setLoadableInState(patchState, 'context'));
  }

  @Action([SetTransactionsFeatureStateStash])
  setTransactionsFeatureStateStash(
    { patchState }: StateContext<ArManagementStateModel>,
    { stashName }: SetTransactionsFeatureStateStash,
  ): void {
    if (stashName == undefined) {
      return;
    }

    const featureStateStashes = this.store.selectSnapshot(ArManagementState.getFeatureStateStashes);
    const arManagementFeatureState = this._getArManagementFeatureState();

    patchState({
      featureStateStashes: {
        ...(featureStateStashes ?? {}),
        [stashName]: arManagementFeatureState,
      },
    });
  }

  @Action([ClearTransactionsFeatureStateStash])
  clearTransactionsFeatureStateStash(
    { patchState }: StateContext<ArManagementStateModel>,
    { stashName }: ClearTransactionsFeatureStateStash,
  ): void {
    const { [stashName]: _, ...featureStateStashes } = this.store.selectSnapshot(ArManagementState.getFeatureStateStashes) ?? {};
    patchState({ featureStateStashes: isEmpty(featureStateStashes) ? undefined : featureStateStashes });
  }

  private _getArManagementCommonState(): ArManagementCommonStateModel {
    const arManagementState = this.store.selectSnapshot((state) => state[AR_MANAGEMENT_STATE_SLICE] as ArManagementStateModel);
    return {
      arTranLabels: arManagementState.arTranLabels,
      unallocatedArTranIdsByClient: arManagementState.unallocatedArTranIdsByClient,
      featureStateStashes: arManagementState.featureStateStashes,
      activeStashName: arManagementState.activeStashName,
    };
  }

  private _getArManagementFeatureState(): ArManagementFeatureStateModel {
    const arManagementState = this.store.selectSnapshot((state) => state[AR_MANAGEMENT_STATE_SLICE] as ArManagementStateModel);
    return {
      context: arManagementState.context,
      transactionsTableExternalFilters: arManagementState.transactionsTableExternalFilters,
      transactionsSidebarMode: arManagementState.transactionsSidebarMode,
      selectedNewTransactionOption: arManagementState.selectedNewTransactionOption,
      areTransactionSidebarChangesPending: arManagementState.areTransactionSidebarChangesPending,
      selectedArTran: arManagementState.selectedArTran,
      linkedArTrans: arManagementState.linkedArTrans,
      linkableArTrans: arManagementState.linkableArTrans,
    };
  }

  //#endregion

  //#region Setup/Mode

  @Action([GetUnallocatedArTranIds])
  getUnallocatedArTranIds(
    { patchState }: StateContext<ArManagementStateModel>,
    { forceRefresh }: GetUnallocatedArTranIds,
  ): Observable<UnallocatedArTranIdsByClient> {
    const unallocatedArTranIdsStatus = this.store.selectSnapshot(ArManagementState.getUnallocatedArTranIdsByClientStatus);
    if (!LoadableHelper.shouldRequest(unallocatedArTranIdsStatus) && !forceRefresh) {
      return;
    }

    return this.arTranApiService.getUnallocatedArTranIdsByClient().pipe(setLoadableInState(patchState, 'unallocatedArTranIdsByClient'));
  }

  @Action([UpdateTransactionsTableExternalFilters])
  updateTransactionsTableExternalFilters(
    { patchState }: StateContext<ArManagementStateModel>,
    { transactionsTableExternalFiltersPartial }: UpdateTransactionsTableExternalFilters,
  ): void {
    const existingTransactionsTableExternalFilters = this.store.selectSnapshot(ArManagementState.getTransactionsTableExternalFilters);
    patchState({
      transactionsTableExternalFilters: {
        ...existingTransactionsTableExternalFilters,
        ...transactionsTableExternalFiltersPartial,
      },
    });
  }

  @Action([SetTransactionsSidebarMode])
  setTransactionsSidebarMode(
    { patchState }: StateContext<ArManagementStateModel>,
    { transactionsSidebarMode }: SetTransactionsSidebarMode,
  ): void {
    const currentTransactionsSidebarMode = this.store.selectSnapshot(ArManagementState.getTransactionsSidebarMode);
    if (transactionsSidebarMode === currentTransactionsSidebarMode) {
      return;
    }

    patchState({
      transactionsSidebarMode,
      ...(currentTransactionsSidebarMode === TransactionsSidebarMode.CREATE ? { selectedNewTransactionOption: undefined } : {}),
      ...(transactionsSidebarMode === TransactionsSidebarMode.VIEW ? { areTransactionSidebarChangesPending: undefined } : {}),
    });
  }

  @Action([GetArTranLabels])
  getArTranLabels({ patchState }: StateContext<ArManagementStateModel>, { forceRefresh }: GetArTranLabels): Observable<unknown> | void {
    const arTranLabelsStatus = this.store.selectSnapshot(ArManagementState.getArTranLabelsStatus);
    if (!(LoadableHelper.shouldRequest(arTranLabelsStatus) || forceRefresh)) {
      return;
    }

    patchState({ arTranLabels: { status: LoadableStatus.LOADING } });

    return this.arTranApiService.getArTranLabels().pipe(setLoadableInState(patchState, 'arTranLabels'));
  }

  @Action([SetSelectedNewTransactionOption])
  setNewTransactionType(
    { patchState }: StateContext<ArManagementStateModel>,
    { newTransactionOption }: SetSelectedNewTransactionOption,
  ): void {
    const currentNewTransactionType = this.store.selectSnapshot(ArManagementState.getSelectedNewTransactionOption);
    if (newTransactionOption == undefined || isEqual(newTransactionOption, currentNewTransactionType)) {
      return;
    }

    patchState({
      selectedNewTransactionOption: newTransactionOption,
      transactionsSidebarMode: TransactionsSidebarMode.CREATE,
    });
  }

  @Action([SetAreTransactionSidebarChangesPending])
  setAreSidebarChangesPending(
    { patchState }: StateContext<ArManagementStateModel>,
    { areSidebarChangesPending }: SetAreTransactionSidebarChangesPending,
  ): void {
    patchState({ areTransactionSidebarChangesPending: areSidebarChangesPending });
  }

  //#endregion

  //#region Selected AR Tran

  @Action([SelectArTran], { cancelUncompleted: true })
  selectArTran({ dispatch, patchState }: StateContext<ArManagementStateModel>, { arTranId }: SelectArTran): Observable<unknown> {
    const selectedArTranLoadable = this.store.selectSnapshot(ArManagementState.getSelectedArTranLoadable);
    if (selectedArTranLoadable.data?.id === arTranId && selectedArTranLoadable.status === LoadableStatus.LOADING) {
      this.logger.warn('ArManagementState : SelectArTran : AR Tran is already being loaded.');
      return;
    }

    patchState({
      transactionsSidebarMode: TransactionsSidebarMode.VIEW,
      selectedArTran: { status: LoadableStatus.LOADING },
      linkedArTrans: DEFAULT_LOADABLE_STATE,
    });

    return this.arTranApiService.getArTransById(arTranId).pipe(
      map((arTran) => {
        if (arTran.type === DebitArTranType.LATE_FEE) {
          return ArManagementState.marshalLateFee(
            arTran as ArTranEntity<DebitArTranType.LATE_FEE>,
            this.store.selectSnapshot(ConfigState.getConfig('lateFeeDisplayName')),
          );
        }

        return arTran;
      }),
      setLoadableInState(patchState, 'selectedArTran'),
      switchMap((_) => dispatch([new GetLinkedArTrans(), new ClearLinkableArTrans()])),
    );
  }

  static marshalLateFee(
    lateFee: ArTranEntity<DebitArTranType.LATE_FEE>,
    lateFeeDisplayName: string,
  ): ArTranEntity<DebitArTranType.LATE_FEE> {
    return {
      ...lateFee,
      typeEntity: {
        ...lateFee.typeEntity,
        typeDisplay: lateFeeDisplayName,
      },
    };
  }

  @Action([RefreshSelectedArTran])
  refreshSelectedArTran(
    { patchState, dispatch }: StateContext<ArManagementStateModel>,
    { refreshLinkedArTrans }: RefreshSelectedArTran,
  ): Observable<unknown> {
    const selectedArTran = this.store.selectSnapshot(ArManagementState.getSelectedArTran);
    if (selectedArTran == undefined) {
      this.logger.error('ArManagementState : RefreshSelectedArTran : No AR Tran selected.');
      return;
    }

    return this.arTranApiService.getArTransById(selectedArTran.id).pipe(
      setLoadableInState(patchState, 'selectedArTran'),
      switchMap((_) => (refreshLinkedArTrans ? dispatch([new GetLinkedArTrans(), new ClearLinkableArTrans()]) : of(undefined))),
    );
  }

  @Action([DeselectArTran])
  deselectArTran({ patchState }: StateContext<ArManagementStateModel>): void {
    const currentTransactionsSidebarMode = this.store.selectSnapshot(ArManagementState.getTransactionsSidebarMode);
    patchState({
      selectedArTran: DEFAULT_LOADABLE_STATE,
      linkedArTrans: DEFAULT_LOADABLE_STATE,
      linkableArTrans: DEFAULT_LOADABLE_STATE,
      ...(currentTransactionsSidebarMode !== TransactionsSidebarMode.CREATE
        ? {
            transactionsSidebarMode: undefined,
            areTransactionSidebarChangesPending: undefined,
          }
        : {}),
    });
  }

  @Action([SaveTransactionNote])
  saveTransactionNote(_: StateContext<ArManagementStateModel>, { note, arTranId }: SaveTransactionNote): Observable<unknown> {
    if (arTranId == undefined) {
      const selectedArTran = this.store.selectSnapshot(ArManagementState.getSelectedArTran);
      arTranId = selectedArTran?.id;
    }

    if (arTranId == undefined) {
      this.logger.error('ArManagementState : SaveTransactionNote : No AR Tran ID provided.');
      return;
    }

    return this.arTranApiService.saveTransactionNote(arTranId, note);
  }

  @Action([SaveTransactionTotal])
  saveTransactionTotal(_: StateContext<ArManagementStateModel>, { total, arTranId }: SaveTransactionTotal): Observable<unknown> {
    if (arTranId == undefined) {
      const selectedArTran = this.store.selectSnapshot(ArManagementState.getSelectedArTran);
      arTranId = selectedArTran?.id;
    }

    if (arTranId == undefined) {
      this.logger.error('ArManagementState : SaveTransactionNote : No AR Tran ID provided.');
      return;
    }

    return this.arTranApiService.saveTransactionTotal(arTranId, total);
  }

  @Action([SetPaymentAsNsf])
  setPaymentAsNsf(_: StateContext<ArManagementStateModel>, { transactionId }: SetPaymentAsNsf): Observable<unknown> {
    if (transactionId == undefined) {
      this.logger.error('ArManagementState : SetPaymentAsNsf : No transaction ID provided.');
      return;
    }

    return this.arTranApiService.setPaymentAsNsf(transactionId);
  }

  @Action([DeactivateTransaction])
  deactivateTransaction(_: StateContext<ArManagementStateModel>, { transactionId }: DeactivateTransaction): Observable<unknown> {
    if (transactionId == undefined) {
      this.logger.error('ArManagementState : DeactivateTransaction : No transaction ID provided.');
      return;
    }

    return this.arTranApiService.deactivateTransaction(transactionId);
  }

  @Action([UpdateTransaction])
  updateTransaction(_: StateContext<ArManagementStateModel>, { updates, arTranId }: UpdateTransaction): Observable<unknown> {
    if (arTranId == undefined) {
      const selectedArTran = this.store.selectSnapshot(ArManagementState.getSelectedArTran);
      arTranId = selectedArTran?.id;
    }

    if (arTranId == undefined) {
      this.logger.error('ArManagementState : UpdateTransaction : No AR Tran ID provided.');
      return;
    }

    return this.arTranApiService.updateTransaction(arTranId, updates);
  }

  //#endregion

  //#region AR Trans Linked to Selected AR Tran

  @Action([GetLinkedArTrans])
  getLinkedArTrans(
    { patchState }: StateContext<ArManagementStateModel>,
    { arTranIds, coreArTranType }: GetLinkedArTrans,
  ): Observable<unknown> {
    const coreArTranBaseType = AbstractArTran.getArTranBaseType(coreArTranType);
    if (typeof arTranIds === 'number' && coreArTranBaseType == undefined) {
      this.logger.error('ArManagementState : GetLinkedArTrans : Called with core AR Tran ID but no type. Must abort.');
      return;
    }

    let linkedArTranIds: number | number[] = arTranIds;

    // use selected AR Tran in state
    if (arTranIds == undefined && coreArTranType == undefined) {
      this.logger.info('ArManagementState : GetLinkedArTrans : Using selected AR Tran in state.');
      const selectedArTran = this.store.selectSnapshot(ArManagementState.getSelectedArTran);
      linkedArTranIds = selectedArTran?.linkedArTranIds;
    }

    if (linkedArTranIds == undefined) {
      this.logger.error('ArManagementState : GetLinkedArTrans : Called without AR Tran IDs nor an AR Tran selected in state. Must abort.');
      return;
    }

    // IDs are already known but core is not selected (not sure when this is useful, but it's possible)
    if (Array.isArray(linkedArTranIds) && linkedArTranIds.length === 0) {
      patchState({
        linkedArTrans: {
          status: LoadableStatus.LOADED,
          data: [],
        },
      });

      return;
    }

    patchState({ linkedArTrans: { status: LoadableStatus.LOADING } });

    const getLinkedArTrans$ =
      typeof linkedArTranIds === 'number'
        ? this.arTranApiService.getLinkedArTrans(linkedArTranIds, coreArTranBaseType)
        : this.arTranApiService.getLinkedArTrans(linkedArTranIds);

    return getLinkedArTrans$.pipe(setLoadableInState(patchState, 'linkedArTrans'));
  }

  @Action([GetLinkableArTrans])
  getLinkableArTrans({ patchState }: StateContext<ArManagementStateModel>, { forceRefresh }: GetLinkableArTrans): Observable<unknown> {
    const linkableArTransStatus = this.store.selectSnapshot(ArManagementState.getLinkableArTransStatus);
    if (!LoadableHelper.shouldRequest(linkableArTransStatus) && !forceRefresh) {
      this.logger.debug(
        `ArManagementState : GetLinkableArTrans : Data is already ${linkableArTransStatus} and forceRefresh is false. Aborting.`,
      );
      return;
    }

    const selectedArTran = this.store.selectSnapshot(ArManagementState.getSelectedArTran);
    if (selectedArTran == undefined) {
      this.logger.error('ArManagementState : GetLinkableArTrans : No AR Tran selected. Must abort.');
      return;
    }

    patchState({ linkableArTrans: { status: LoadableStatus.LOADING } });

    const linkedArTranIds = selectedArTran.linkedArTranIds ?? [];

    return this.arTranApiService.getLinkableArTransForClient(selectedArTran.clientId, selectedArTran.linkableTypes).pipe(
      map((arTrans) => arTrans.filter((arTran) => !linkedArTranIds.includes(arTran.id))),
      setLoadableInState(patchState, 'linkableArTrans'),
    );
  }

  @Action([ClearLinkableArTrans])
  clearLinkableArTrans({ patchState }: StateContext<ArManagementStateModel>): void {
    patchState({ linkableArTrans: DEFAULT_LOADABLE_STATE });
  }

  @Action([SaveTransactionLinks])
  saveTransactionLinks(_: StateContext<ArManagementStateModel>, { newTransactionLinks }: SaveTransactionLinks): Observable<unknown> {
    // TODO: Add logic for unselected AR Tran
    const selectedArTran = this.store.selectSnapshot(ArManagementState.getSelectedArTran);
    if (selectedArTran == undefined) {
      this.logger.error('ArManagementState : SaveTransactionLinks : No AR Tran selected.');
      return;
    }

    return this.arTranApiService
      .saveTransactionLinks(selectedArTran, newTransactionLinks)
      .pipe(switchMap((_) => this.store.dispatch(new ClearLinkableArTrans())));
  }

  //#endregion

  //#region Transaction Creation

  @Action([CreateTransaction])
  createTransaction(_: StateContext<ArManagementStateModel>, { newTransaction }: CreateTransaction): Observable<unknown> {
    if (newTransaction == undefined) {
      this.logger.error('ArManagementState : CreateTransaction : No new transaction provided.');
      return;
    }

    return this.arTranApiService.createArTran(newTransaction);
  }

  //#endregion

  //#endregion
}
